import { memo, useCallback, useMemo, useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Api } from "../../services/interceptor";
import { QUERY_KEY } from "../../services/queryEndPoint";
import { resolvePublicImageUrl } from "../../helper/helper";
import { Images } from "../../utils/imageConstants";
import type { ITeamsSquad } from "../leaguesHome/tournaments/tournamentsDetails/matches/matchesDetails/types/matchDetails.d";
import toast from "react-hot-toast";
import "./AssetSetup.scss";

// ── Constants ──────────────────────────────────────────────────────────────────

const CROP_OUTPUT_W = 600;
const CROP_OUTPUT_H = 720;

const PRESIGNED_URLS_ENDPOINT = "/uploads/presigned-urls";
const TICKER_ASSETS_ENDPOINT = "/tournament/ticker/assets";
// Was hardcoded to prod's ticker-assets CDN regardless of env, so an asset uploaded via QA
// (which the backend's presigned URL correctly scopes to QA's own ticker-assets bucket) was
// then displayed through prod's CDN pointing at prod's bucket, where the file doesn't exist -
// a 403 Forbidden, not a 404, since S3 returns 403 for an object a caller has no ListBucket
// permission to even confirm is absent. VITE_TICKER_ASSETS_CDN_URL is deliberately its own
// variable, not VITE_TICKER_BASE_URL (the ticker-overlay app's own hosting CDN) - the two
// happen to be the same bucket/distribution in QA, but are two separate buckets in prod.
const TICKER_CDN_BASE = import.meta.env.VITE_TICKER_ASSETS_CDN_URL as string;

const ACCEPTED_MIME = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const MAX_FILE_BYTES = 5 * 1024 * 1024; // 5 MB

// Same purple the Custom Colors theme's own "Classic" preset uses as team one's default
// (StreamerDashboard.tsx's CUSTOM_COLOR_PRESETS[0]), so a team with no color saved yet shows
// the same starting point a streamer would otherwise see on a match with no color picked.
const DEFAULT_TEAM_COLOR = "#582F83";

// ── Types ──────────────────────────────────────────────────────────────────────

type TickerAssetType = "HEAD_SHOT" | "PROFILE_IMAGE";

interface TickerAssetPayload {
  playerId: string;
  teamSnapShotId: string;
  assetType: TickerAssetType;
  assetUrl: string;
}

interface SaveTickerAssetsRequest {
  tournamentId: string;
  assets: TickerAssetPayload[];
}

async function saveTickerAssets(payload: SaveTickerAssetsRequest): Promise<void> {
  await Api.post(TICKER_ASSETS_ENDPOINT, payload);
}

interface AssetSetupState {
  leagueId: string;
  leagueName: string;
  tournamentId: string;
  tournamentName: string;
}

interface TeamSnapshot {
  teamSnapshotId: string;
  teamName: string;
  imageUrl?: string;
  description?: string;
  teamCode?: string;
  // The team's permanent identity (DivisionResponse's nested TeamResponse) - distinct from
  // teamSnapshotId, which is scoped to this one tournament. Needed to read/write the team's
  // own default ticker color, which lives on Team, not TeamSnapshot.
  team?: { teamId?: string };
}

interface DivisionResponse {
  divisionId?: string;
  divisionName?: string;
  childDivisions?: DivisionResponse[];
  teams?: TeamSnapshot[];
}

interface TournamentDivisionsResponse {
  tournamentId?: string;
  divisionResponseList?: DivisionResponse[];
  canEdit?: boolean;
  canDelete?: boolean;
}

type UploadStatus = "idle" | "uploading" | "success" | "error";
type ImageType = "headShot" | "profile";

interface PlayerTeamSnapshotAssociation {
  id: number;
  player: {
    playerId: string;
    playerFirstName?: string;
    playerLastName?: string;
    image?: string | null;
    gender?: string | null;
    battingStyle?: string | null;
    bowlingStyle?: string | null;
  };
  teamSnapshot: {
    teamSnapshotId: string;
    teamName: string;
    imageUrl?: string | null;
  };
  role?: { id: string };
  headShotImageUrl?: string | null;
  profileImageUrl?: string | null;
}

type TickerAssetsMap = Record<string, PlayerTeamSnapshotAssociation[]>;

interface LightboxState {
  src: string;
  label: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function buildTickerUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  return `${TICKER_CDN_BASE}${path.replace(/^\//, "")}`;
}

function collectTeams(divisions: DivisionResponse[]): TeamSnapshot[] {
  const teams: TeamSnapshot[] = [];
  const seen = new Set<string>();
  const traverse = (list: DivisionResponse[]) => {
    for (const div of list) {
      for (const team of div.teams ?? []) {
        if (!seen.has(team.teamSnapshotId)) {
          seen.add(team.teamSnapshotId);
          teams.push(team);
        }
      }
      if (div.childDivisions?.length) traverse(div.childDivisions);
    }
  };
  traverse(divisions);
  return teams;
}

function dedupeByPlayerId(players: ITeamsSquad[]): ITeamsSquad[] {
  const seen = new Set<string>();
  const out: ITeamsSquad[] = [];
  for (const p of players) {
    const id = p.playerId?.trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(p);
  }
  return out;
}

function getInitials(first?: string, last?: string): string {
  const f = (first ?? "").trim().charAt(0).toUpperCase();
  const l = (last ?? "").trim().charAt(0).toUpperCase();
  return f && l ? `${f}${l}` : f || l || "–";
}

function validateFile(file: File): string | null {
  if (!ACCEPTED_MIME.includes(file.type)) {
    return `Unsupported file type "${file.type || file.name.split(".").pop()}". Please upload a JPG, PNG, or WEBP image.`;
  }
  if (file.size > MAX_FILE_BYTES) {
    return `File is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Maximum allowed size is 5 MB.`;
  }
  return null;
}

// ── Image lightbox ─────────────────────────────────────────────────────────────

const ImageLightbox = memo(({
  state,
  onClose,
}: {
  state: LightboxState;
  onClose: () => void;
}) => {
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [onClose]);

  return (
    <div
      className="asset-setup__lightbox-backdrop"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={state.label}
    >
      <div
        className="asset-setup__lightbox-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="asset-setup__lightbox-close"
          onClick={onClose}
          aria-label="Close preview"
        >
          ✕
        </button>
        <p className="asset-setup__lightbox-label">{state.label}</p>
        <img
          src={state.src}
          alt={state.label}
          className="asset-setup__lightbox-img"
        />
      </div>
    </div>
  );
});

// ── Image cropper modal ────────────────────────────────────────────────────────

interface ImageCropperModalProps {
  file: File;
  onConfirm: (blob: Blob) => void;
  onCancel: () => void;
}

const ImageCropperModal = memo(({ file, onConfirm, onCancel }: ImageCropperModalProps) => {
  const frameRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const dragRef = useRef<{ mx: number; my: number; px: number; py: number } | null>(null);

  const [srcUrl, setSrcUrl] = useState("");
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [userScale, setUserScale] = useState(1);
  const [baseScale, setBaseScale] = useState(1);

  useEffect(() => {
    const url = URL.createObjectURL(file);
    setSrcUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Non-passive wheel listener so preventDefault works
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const handler = (e: WheelEvent) => {
      e.preventDefault();
      setUserScale((s) => Math.min(Math.max(s - e.deltaY * 0.001, 0.5), 4));
    };
    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, []);

  useEffect(() => {
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") onCancel(); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [onCancel]);

  const getFrameSize = () => {
    const r = frameRef.current?.getBoundingClientRect();
    return { w: r?.width ?? 420, h: r?.height ?? 504 };
  };

  const handleImgLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    imgRef.current = img;
    const { w, h } = getFrameSize();
    setBaseScale(Math.max(w / img.naturalWidth, h / img.naturalHeight));
    setUserScale(1);
    setPos({ x: 0, y: 0 });
  }, []);

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { mx: e.clientX, my: e.clientY, px: pos.x, py: pos.y };
  }, [pos]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    setPos({
      x: dragRef.current.px + (e.clientX - dragRef.current.mx),
      y: dragRef.current.py + (e.clientY - dragRef.current.my),
    });
  }, []);

  const handlePointerUp = useCallback(() => { dragRef.current = null; }, []);

  const handleConfirm = useCallback(() => {
    const img = imgRef.current;
    if (!img) return;

    const { w: dW, h: dH } = getFrameSize();
    const totalScale = baseScale * userScale;

    const imgLeft = dW / 2 + pos.x - (img.naturalWidth * totalScale) / 2;
    const imgTop  = dH / 2 + pos.y - (img.naturalHeight * totalScale) / 2;

    const srcX = -imgLeft / totalScale;
    const srcY = -imgTop  / totalScale;
    const srcW = dW / totalScale;
    const srcH = dH / totalScale;

    const canvas = document.createElement("canvas");
    canvas.width  = CROP_OUTPUT_W;
    canvas.height = CROP_OUTPUT_H;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(img, srcX, srcY, srcW, srcH, 0, 0, CROP_OUTPUT_W, CROP_OUTPUT_H);
    const mimeType = (file.type === "image/png" || file.type === "image/webp") ? "image/png" : "image/jpeg";
    canvas.toBlob((blob) => { if (blob) onConfirm(blob); }, mimeType, mimeType === "image/jpeg" ? 0.92 : undefined);
  }, [baseScale, userScale, pos, onConfirm, file.type]);

  return (
    <div
      className="img-cropper__backdrop"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-label="Crop image"
    >
      <div className="img-cropper__modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="img-cropper__close"
          onClick={onCancel}
          aria-label="Close cropper"
        >
          ✕
        </button>

        <div>
          <p className="img-cropper__title">Crop Image</p>
          <p className="img-cropper__hint">
            Drag to reposition · Scroll or use slider to zoom · Output: 600 × 720 px
          </p>
        </div>

        <div
          ref={frameRef}
          className="img-cropper__frame"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        >
          {srcUrl && (
            <img
              src={srcUrl}
              alt="Crop preview"
              className="img-cropper__image"
              onLoad={handleImgLoad}
              draggable={false}
              style={{
                position: "absolute",
                left: "50%",
                top: "50%",
                transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px)) scale(${baseScale * userScale})`,
                transformOrigin: "center center",
              }}
            />
          )}
        </div>

        <div className="img-cropper__zoom-row">
          <button
            type="button"
            className="img-cropper__zoom-btn"
            onClick={() => setUserScale((s) => Math.max(+(s - 0.1).toFixed(2), 0.5))}
            aria-label="Zoom out"
          >
            −
          </button>
          <input
            type="range"
            className="img-cropper__zoom-slider"
            min={50}
            max={400}
            value={Math.round(userScale * 100)}
            onChange={(e) => setUserScale(Number(e.target.value) / 100)}
            aria-label="Zoom level"
          />
          <button
            type="button"
            className="img-cropper__zoom-btn"
            onClick={() => setUserScale((s) => Math.min(+(s + 0.1).toFixed(2), 4))}
            aria-label="Zoom in"
          >
            +
          </button>
          <span className="img-cropper__zoom-value">{Math.round(userScale * 100)}%</span>
        </div>

        <div className="img-cropper__actions">
          <button type="button" className="img-cropper__cancel-btn" onClick={onCancel}>
            Cancel
          </button>
          <button type="button" className="img-cropper__confirm-btn" onClick={handleConfirm}>
            Confirm &amp; Upload
          </button>
        </div>
      </div>
    </div>
  );
});

// ── Image upload cell ──────────────────────────────────────────────────────────

interface ImageUploadCellProps {
  playerId: string;
  teamSnapshotId: string;
  tournamentId: string;
  type: ImageType;
  existingUrl?: string | null;
  playerName: string;
  onOpenPreview: (src: string, label: string) => void;
}

const IMAGE_TYPE_TO_ASSET_TYPE: Record<ImageType, TickerAssetType> = {
  headShot: "HEAD_SHOT",
  profile: "PROFILE_IMAGE",
};

const ImageUploadCell = memo(({
  playerId,
  teamSnapshotId,
  tournamentId,
  type,
  existingUrl,
  playerName,
  onOpenPreview,
}: ImageUploadCellProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(
    existingUrl ? resolvePublicImageUrl(existingUrl) : null
  );
  const [status, setStatus] = useState<UploadStatus>("idle");
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const objectUrlRef = useRef<string | null>(null);

  // Clean up any object URL on unmount
  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  // File selected → open cropper instead of uploading immediately
  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      e.target.value = "";

      const validationError = validateFile(file);
      if (validationError) {
        toast.error(validationError);
        return;
      }

      setPendingFile(file);
    },
    []
  );

  // Called after the user confirms the crop
  const handleCropConfirm = useCallback(
    async (blob: Blob, originalFile: File) => {
      setPendingFile(null);

      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      const localUrl = URL.createObjectURL(blob);
      objectUrlRef.current = localUrl;
      setPreviewUrl(localUrl);
      setStatus("uploading");

      try {
        const clientImageId = crypto.randomUUID();
        const contentType = blob.type || "image/jpeg";
        const ext = contentType === "image/png" ? "png" : "jpg";
        const croppedName = originalFile.name.replace(/\.[^.]+$/, `.${ext}`);

        const { data } = await Api.post(PRESIGNED_URLS_ENDPOINT, {
          files: [
            {
              clientImageId,
              fileName: croppedName,
              contentType,
              fileSize: blob.size,
            },
          ],
          bucketName: "TICKER_ASSET_BUCKET",
        });

        const upload = (data.uploads as Array<{ clientImageId: string; uploadUrl: string; uploadId: string; s3Key: string }>)
          .find((u) => u.clientImageId === clientImageId);

        if (!upload?.uploadUrl) throw new Error("No upload URL returned");

        await fetch(upload.uploadUrl, {
          method: "PUT",
          body: blob,
          headers: { "Content-Type": contentType },
        });

        await saveTickerAssets({
          tournamentId,
          assets: [
            {
              playerId,
              teamSnapShotId: teamSnapshotId,
              assetType: IMAGE_TYPE_TO_ASSET_TYPE[type],
              assetUrl: upload.s3Key,
            },
          ],
        });

        setStatus("success");
      } catch {
        setStatus("error");
        toast.error("Upload failed. Please try again.");
      }
    },
    [playerId, teamSnapshotId, tournamentId, type]
  );

  const label = `${playerName} — ${type === "headShot" ? "Head Shot" : "Profile Image"}`;

  const handleThumbnailClick = useCallback(() => {
    if (previewUrl) onOpenPreview(previewUrl, label);
  }, [previewUrl, label, onOpenPreview]);

  return (
    <div className="asset-setup__img-cell">
      {pendingFile && (
        <ImageCropperModal
          file={pendingFile}
          onConfirm={(blob) => handleCropConfirm(blob, pendingFile)}
          onCancel={() => setPendingFile(null)}
        />
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/webp"
        className="asset-setup__img-input"
        onChange={handleFileChange}
        aria-label={`Upload ${label}`}
      />

      {previewUrl ? (
        <div className="asset-setup__img-preview-wrap">
          <button
            type="button"
            className="asset-setup__img-thumbnail-btn"
            onClick={handleThumbnailClick}
            title="Click to enlarge"
          >
            <img
              src={previewUrl}
              alt={label}
              className="asset-setup__img-thumbnail"
            />
          </button>
          <div className="asset-setup__img-actions">
            {status === "uploading" && (
              <span className="asset-setup__img-status asset-setup__img-status--uploading">
                <span className="asset-setup__spinner" />
                Uploading…
              </span>
            )}
            {status === "success" && (
              <span className="asset-setup__img-status asset-setup__img-status--success">
                ✓ Saved
              </span>
            )}
            {status === "error" && (
              <span className="asset-setup__img-status asset-setup__img-status--error">
                ✗ Failed
              </span>
            )}
            <button
              type="button"
              className="asset-setup__img-change-btn"
              onClick={() => inputRef.current?.click()}
              disabled={status === "uploading"}
            >
              Change
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className="asset-setup__img-upload-btn"
          onClick={() => inputRef.current?.click()}
          title={`Upload ${type === "headShot" ? "head shot" : "profile image"}`}
        >
          <svg
            className="asset-setup__img-upload-icon"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          <span>Upload</span>
          <span className="asset-setup__img-hint">JPG, PNG, WEBP · max 5 MB</span>
        </button>
      )}
    </div>
  );
});

// ── Team list item ─────────────────────────────────────────────────────────────

const TeamListItem = memo(({
  team,
  isActive,
  onClick,
}: {
  team: TeamSnapshot;
  isActive: boolean;
  onClick: () => void;
}) => {
  const src = resolvePublicImageUrl(team.imageUrl ?? null) || Images.card_info_image;
  return (
    <button
      type="button"
      className={`asset-setup__team-item${isActive ? " asset-setup__team-item--active" : ""}`}
      onClick={onClick}
    >
      <div className="asset-setup__team-item-avatar">
        <img
          src={src}
          alt={team.teamName}
          onError={(e) => {
            if (e.currentTarget.src !== Images.card_info_image)
              e.currentTarget.src = Images.card_info_image;
          }}
        />
      </div>
      <div className="asset-setup__team-item-info">
        <span className="asset-setup__team-item-name">{team.teamName}</span>
        {team.teamCode && (
          <span className="asset-setup__team-item-code">{team.teamCode}</span>
        )}
      </div>
    </button>
  );
});

// ── Skeleton components ────────────────────────────────────────────────────────

const TeamListSkeleton = () => (
  <>
    {Array.from({ length: 6 }).map((_, i) => (
      <div key={i} className="asset-setup__team-item asset-setup__team-item--skeleton">
        <div className="asset-setup__team-item-avatar asset-setup__skeleton-block" />
        <div className="asset-setup__team-item-info">
          <div className="asset-setup__skeleton-block asset-setup__skeleton-name" />
          <div className="asset-setup__skeleton-block asset-setup__skeleton-code" />
        </div>
      </div>
    ))}
  </>
);

const PlayersTableSkeleton = () => (
  <tbody>
    {Array.from({ length: 6 }).map((_, i) => (
      <tr key={i} className="asset-setup__table-row--skeleton">
        <td>
          <div className="asset-setup__player-cell">
            <div className="asset-setup__player-avatar asset-setup__skeleton-block" />
            <div className="asset-setup__skeleton-block asset-setup__skeleton-name" />
          </div>
        </td>
        <td><div className="asset-setup__skeleton-block asset-setup__skeleton-img-cell" /></td>
        <td><div className="asset-setup__skeleton-block asset-setup__skeleton-img-cell" /></td>
      </tr>
    ))}
  </tbody>
);

// ── Main component ─────────────────────────────────────────────────────────────

const AssetSetup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const state = location.state as AssetSetupState | null;
  const tournamentId = state?.tournamentId;

  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);

  const { data: divisionsData, isLoading: teamsLoading } =
    useQuery<TournamentDivisionsResponse>({
      queryKey: ["getDivisions", tournamentId],
      queryFn: async () => {
        const res = await Api.get(`/getDivisions/${tournamentId}`);
        return res.data;
      },
      enabled: !!tournamentId,
    });

  const teams = useMemo(
    () => collectTeams(divisionsData?.divisionResponseList ?? []),
    [divisionsData]
  );

  useEffect(() => {
    if (!selectedTeamId && teams.length > 0) {
      setSelectedTeamId(teams[0].teamSnapshotId);
    }
  }, [teams, selectedTeamId]);

  const { data: rawPlayers = [], isLoading: playersLoading } = useQuery<ITeamsSquad[]>({
    queryKey: [QUERY_KEY.getAllPlayers, selectedTeamId],
    queryFn: async () => {
      const res = await Api.get(`${QUERY_KEY.getAllPlayers}/${selectedTeamId}`);
      return (res.data ?? []) as ITeamsSquad[];
    },
    enabled: !!selectedTeamId,
  });

  // Fetch ticker assets once per tournament — staleTime: Infinity prevents refetches
  const { data: tickerAssetsMap = {} } = useQuery<TickerAssetsMap>({
    queryKey: ["tickerAssets", tournamentId],
    queryFn: async () => {
      const res = await Api.get(`${TICKER_ASSETS_ENDPOINT}?tournamentId=${tournamentId}`);
      return (res.data ?? {}) as TickerAssetsMap;
    },
    enabled: !!tournamentId,
    staleTime: Infinity,
  });

  const players = useMemo(() => dedupeByPlayerId(rawPlayers), [rawPlayers]);
  const selectedTeam = teams.find((t) => t.teamSnapshotId === selectedTeamId);
  const selectedTeamPermanentId = selectedTeam?.team?.teamId;

  // The team's own default ticker color (Team.teamColor, not per-match) - set once here, it
  // seeds teamOneColor/teamTwoColor on every new match this team plays in going forward
  // (MatchService.applyDefaultTeamColors). Refetched per team so switching teams in the
  // sidebar always shows that team's own saved color, never a stale one left over from
  // whichever team was selected before.
  const { data: teamColorData } = useQuery<{ teamColor: string | null }>({
    queryKey: ["teamColor", selectedTeamPermanentId],
    queryFn: async () => {
      const res = await Api.get(`/team/${selectedTeamPermanentId}/color`);
      return res.data;
    },
    enabled: !!selectedTeamPermanentId,
  });

  const [teamColorInput, setTeamColorInput] = useState(DEFAULT_TEAM_COLOR);
  const [savingTeamColor, setSavingTeamColor] = useState(false);

  useEffect(() => {
    setTeamColorInput(teamColorData?.teamColor || DEFAULT_TEAM_COLOR);
  }, [teamColorData, selectedTeamPermanentId]);

  const saveTeamColor = async () => {
    if (!selectedTeamPermanentId) return;
    setSavingTeamColor(true);
    try {
      await Api.put(`/team/${selectedTeamPermanentId}/color`, { teamColor: teamColorInput });
      toast.success(`Saved ${selectedTeam?.teamName}'s default color`);
    } catch {
      toast.error("Could not save the team color. Please try again.");
    } finally {
      setSavingTeamColor(false);
    }
  };

  // Index the ticker assets for the active team by playerId for O(1) lookup
  const teamTickerIndex = useMemo<Record<string, PlayerTeamSnapshotAssociation>>(() => {
    if (!selectedTeamId) return {};
    const assocs = tickerAssetsMap[selectedTeamId] ?? [];
    return Object.fromEntries(assocs.map((a) => [a.player.playerId, a]));
  }, [tickerAssetsMap, selectedTeamId]);

  const openPreview = useCallback((src: string, label: string) => {
    setLightbox({ src, label });
  }, []);

  const closeLightbox = useCallback(() => setLightbox(null), []);

  return (
    <div className="asset-setup">
      {lightbox && (
        <ImageLightbox state={lightbox} onClose={closeLightbox} />
      )}

      <div className="asset-setup__header">
        <button className="asset-setup__back-btn" onClick={() => navigate(-1)}>
          ← Back
        </button>
        <h1 className="asset-setup__title">Asset Setup</h1>
      </div>

      <div className="asset-setup__selection-info">
        <div className="asset-setup__info-card">
          <span className="asset-setup__info-label">League</span>
          <span className="asset-setup__info-value">{state?.leagueName ?? "—"}</span>
        </div>
        <div className="asset-setup__info-card">
          <span className="asset-setup__info-label">Tournament</span>
          <span className="asset-setup__info-value">{state?.tournamentName ?? "—"}</span>
        </div>
      </div>

      <div className="asset-setup__body">
        {/* ── Left: team list ── */}
        <aside className="asset-setup__sidebar">
          <div className="asset-setup__sidebar-header">
            <span className="asset-setup__sidebar-title">Teams</span>
            {!teamsLoading && (
              <span className="asset-setup__section-count">{teams.length}</span>
            )}
          </div>
          <div className="asset-setup__team-list">
            {teamsLoading ? (
              <TeamListSkeleton />
            ) : teams.length === 0 ? (
              <p className="asset-setup__sidebar-empty">No teams found.</p>
            ) : (
              teams.map((team) => (
                <TeamListItem
                  key={team.teamSnapshotId}
                  team={team}
                  isActive={team.teamSnapshotId === selectedTeamId}
                  onClick={() => setSelectedTeamId(team.teamSnapshotId)}
                />
              ))
            )}
          </div>
        </aside>

        {/* ── Right: players table ── */}
        <div className="asset-setup__content">
          <div className="asset-setup__content-header">
            <div className="asset-setup__content-title-row">
              <h2 className="asset-setup__content-title">
                {selectedTeam?.teamName ?? "Players"}
              </h2>
              {!playersLoading && selectedTeam && (
                <span className="asset-setup__section-count">{players.length}</span>
              )}
            </div>
            {selectedTeam?.teamCode && (
              <span className="asset-setup__content-subtitle">{selectedTeam.teamCode}</span>
            )}
            {selectedTeamPermanentId && (
              <div className="asset-setup__team-color">
                <label className="asset-setup__team-color-label">
                  Default ticker color
                  <input
                    type="color"
                    value={teamColorInput}
                    onChange={(e) => setTeamColorInput(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  className="asset-setup__team-color-save"
                  disabled={savingTeamColor}
                  onClick={saveTeamColor}
                >
                  {savingTeamColor ? "Saving…" : "Save Color"}
                </button>
                <span className="asset-setup__team-color-hint">
                  Used as this team's color on every new match, until a streamer picks a different one.
                </span>
              </div>
            )}
          </div>

          <div className="asset-setup__table-wrap">
            <table className="asset-setup__players-table">
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Head Shot Image</th>
                  <th>Profile Image</th>
                </tr>
              </thead>

              {playersLoading ? (
                <PlayersTableSkeleton />
              ) : players.length === 0 ? (
                <tbody>
                  <tr>
                    <td colSpan={3}>
                      <div className="asset-setup__table-empty">
                        No players found for this team.
                      </div>
                    </td>
                  </tr>
                </tbody>
              ) : (
                <tbody>
                  {players.map((player) => {
                    const fullName =
                      [player.playerFirstName, player.playerMiddleName, player.playerLastName]
                        .filter(Boolean)
                        .join(" ") || "—";
                    const avatarSrc = resolvePublicImageUrl(player.imageUri) || null;
                    const initials = getInitials(player.playerFirstName, player.playerLastName);

                    const tickerAsset = teamTickerIndex[player.playerId];
                    const headShotUrl = buildTickerUrl(tickerAsset?.headShotImageUrl);
                    const profileUrl  = buildTickerUrl(tickerAsset?.profileImageUrl);

                    return (
                      <tr key={player.playerId}>
                        <td>
                          <div className="asset-setup__player-cell">
                            <div className="asset-setup__player-avatar">
                              {avatarSrc ? (
                                <img
                                  src={avatarSrc}
                                  alt={fullName}
                                  onError={(e) => {
                                    e.currentTarget.style.display = "none";
                                    const next = e.currentTarget
                                      .nextElementSibling as HTMLElement | null;
                                    if (next) next.style.display = "flex";
                                  }}
                                />
                              ) : null}
                              <span
                                className="asset-setup__player-avatar-initials"
                                style={{ display: avatarSrc ? "none" : "flex" }}
                              >
                                {initials}
                              </span>
                            </div>
                            <span className="asset-setup__player-name">{fullName}</span>
                          </div>
                        </td>
                        <td>
                          <ImageUploadCell
                            playerId={player.playerId}
                            teamSnapshotId={selectedTeamId!}
                            tournamentId={tournamentId!}
                            type="headShot"
                            existingUrl={headShotUrl}
                            playerName={fullName}
                            onOpenPreview={openPreview}
                          />
                        </td>
                        <td>
                          <ImageUploadCell
                            playerId={player.playerId}
                            teamSnapshotId={selectedTeamId!}
                            tournamentId={tournamentId!}
                            type="profile"
                            existingUrl={profileUrl}
                            playerName={fullName}
                            onOpenPreview={openPreview}
                          />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssetSetup;
